import React, { useState, useEffect, useCallback } from 'react';
import Main from './Main';
import Axios from 'axios';
import { Grid } from "@material-ui/core";
import { Spinner, Card, Typography, Input, Button, Tooltip, Dialog, DialogHeader, DialogBody, DialogFooter } from '@material-tailwind/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Box } from "@material-ui/core";
import { transformarFechaTramiteSimple } from '../Helpers/fechas';

export default function IglesiasDelegados() {
    const [originalList, setOriginalList] = useState([]);
    const [delegadosList, setDelegadosList] = useState([]);
    const [nameSearch, setNameSearch] = useState('');
    const [buscandoDatos, setBuscandoDatos] = useState(false);
    
    const [open, setOpen] = useState(false);
    const [cabecera, setCabecera] = useState({});
    const [delegados, setDelegados] = useState([]);

    useEffect(() => {
        obtenerArchivos();
    }, []);

    const handleSearchData = useCallback(() => {
        if (nameSearch.trim() === '') {
            setDelegadosList(originalList);
        } else {
            const searchLower = nameSearch.toLowerCase();
            const listaFiltrada = originalList.filter(item =>
                item.nombre?.toLowerCase().includes(searchLower) ||
                item.apellido?.toLowerCase().includes(searchLower) ||
                item.iglesia?.toLowerCase().includes(searchLower) ||
                item.pastor?.toLowerCase().includes(searchLower) ||
                item.nro_documento?.toString().includes(searchLower)
            );
            setDelegadosList(listaFiltrada.length > 0 ? listaFiltrada : originalList);
        }
    }, [nameSearch, originalList]);

    const handleOpen = () => setOpen(!open);

    const obtenerArchivos = async () => {
        setBuscandoDatos(true);
        try {
            const result = await Promise.all([Axios.get(`/api/tablasgenerales/delegados/all`)]);
            setOriginalList(result[0].data);
            setDelegadosList(result[0].data);
            setBuscandoDatos(false);
        } catch (error) {
            console.log(error);
            setBuscandoDatos(false);
        }
    };

    const limpiarBusqueda = useCallback(() => {
        setNameSearch('');
        setDelegadosList(originalList);
    }, [originalList]);

    // Lógica de teclas (Enter y Escape)
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Enter') {
                event.preventDefault(); 
                handleSearchData();
            }
            if (event.key === 'Escape') {
                limpiarBusqueda();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleSearchData, limpiarBusqueda]);

    if (buscandoDatos) {
        return <Main center alinear>
            <div className='flex flex-col justify-center items-center h-[calc(100vh-10rem)]'>
                <Spinner className="h-12 w-12" />
                <span className="cargando font-semibold text-[15px]">Cargando delegados...</span>
            </div>
        </Main>
    }

    if (originalList.length === 0 && !buscandoDatos) {
        return <Grid container justifyContent='center' alignItems='center' style={{ minHeight: '400px', textAlign: 'center' }} >
            <Grid item>
                <Typography variant="h5">No se encontraron delegados</Typography>
            </Grid>
        </Grid>
    }

    const TABLE_HEAD = ["Nombre Completo", "Fecha Alta", "DNI", "Iglesia", "Localidad", "Pastor", "Observaciones"];

    return (
        <div className='w-full flex flex-col justify-center items-center lg:!m-6 xl:!m-6'>
            <Typography variant="h4" className="flex">Delegados</Typography>
            
            <Card className='w-full justify-center items-center flex p-5 justify-left mt-4'>
                <div className='w-full lg:w-96 xl:w-96 relative'>
                    <Input
                        type="text"
                        label="Buscar por nombre, iglesia, pastor o DNI"
                        value={nameSearch}
                        onChange={(e) => setNameSearch(e.target.value)}
                        color='black'
                    />
                    <Button
                        size="sm"
                        color={nameSearch ? "blue" : "blue-gray"}
                        disabled={!nameSearch}
                        className="!absolute right-1 top-1 rounded flex"
                        onClick={handleSearchData}
                    >
                        <MagnifyingGlassIcon className="h-4 w-4 mr-2" />Buscar
                    </Button>
                </div>

                {delegadosList.length > 0 && (
                    <div className='mt-8 w-full'>
                        <div className="flex flex-col md:flex-row gap-2 xl:!justify-end">
                            <Button variant='text' className='text-red-400 bg-red-100 hover:bg-red-200' onClick={limpiarBusqueda}>
                                Limpiar Busqueda
                            </Button>
                        </div>
                        
                        <Typography variant="small" color="blue-gray" className="font-normal leading-none mt-2">
                            Se han encontrado {delegadosList.length} delegados registrados
                        </Typography>

                        <table className="w-full min-w-max table-auto text-left mt-2">
                            <thead>
                                <tr>
                                    {TABLE_HEAD.map((head) => (
                                        <th key={head} className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                                            <Typography variant="small" color="blue-gray" className="font-normal leading-none opacity-70">
                                                {head}
                                            </Typography>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {delegadosList.map((item, index) => {
                                    const isLast = index === delegadosList.length - 1;
                                    const classes = isLast ? "p-4" : "p-4 border-b border-blue-gray-50";

                                    return (
                                        <tr key={index}>
                                            <td className={classes}>
                                                <Typography variant="small" color="blue-gray" className="font-normal">
                                                    {item.nombre} {item.apellido}
                                                </Typography>
                                            </td>
                                            <td className={classes}>
                                                <Typography variant="small" color="blue-gray" className="font-normal">
                                                    {item.fecha_alta ? transformarFechaTramiteSimple(item.fecha_alta) : ''}
                                                </Typography>
                                            </td>
                                            <td className={classes}>
                                                <Typography variant="small" color="blue-gray" className="font-normal">
                                                    {item.nro_documento}
                                                </Typography>
                                            </td>
                                            <td className={classes}>
                                                <Typography variant="small" color="blue-gray" className="font-normal">
                                                    {item.iglesia}
                                                </Typography>
                                            </td>
                                            <td className={classes}>
                                                <Typography variant="small" color="blue-gray" className="font-normal">
                                                    {item.localidad}
                                                </Typography>
                                            </td>
                                            <td className={classes}>
                                                <Typography variant="small" color="blue-gray" className="font-normal">
                                                    {item.pastor}
                                                </Typography>
                                            </td>
                                            {/* COLUMNA OBSERVACIONES REEMPLAZANDO ACCIONES */}
                                            <td className={classes}>
                                                {item.observaciones ? (
                                                    <Tooltip content={item.observaciones}>
                                                        <Typography variant="small" color="blue-gray" className="font-normal cursor-help max-w-[200px] truncate">
                                                            {item.observaciones}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="small" color="gray" className="font-normal italic opacity-40">
                                                        -
                                                    </Typography>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}